import { useCallback, useEffect, useMemo } from 'react';
import queryString from 'query-string';
import { CountryCode } from 'libphonenumber-js';

import { COLO_AUTHENTICATION_ENABLED, GIFTCARD_PAY_ENABLED, PERKS_ENABLED, Status } from './models';
import { ServiceType } from 'hooks/context/serviceType/models';
import { getValidTimezone } from 'util/Date';
import { MerchantDetails } from 'olo-service';
import { DEFAULT_LOCALE_CODE } from 'i18n/constants';
import { getCountryCode } from 'i18n/helpers';
import { fromCents, isPhoneNumberValid } from 'util/Formatting';
import { createHookContext } from '@clover-util/react-context';
import { isNotFoundError } from 'util/axiosHelpers';
import { trackMerchant } from 'util/Analytics';
import { useGetMerchantDetailsResource, isSlug } from 'hooks/useApi';
import { useSmartNavigate } from 'hooks/useSmartNavigate';
import { useTranslation } from 'react-i18next';

// context to hold merchant info such as business info- address, hours, etc

// Allow overriding states for testing locally
const STATUS_OVERRIDE: Status | undefined = (() => {
  // Wrap in a NODE_ENV check so this code block will not be included in the prod bundle
  if (process.env.NODE_ENV === 'development') {
    if (!window.location.search) return;

    const value = queryString.parse(window.location.search)['status'];

    if (!value) return;

    return (Array.isArray(value) ? value[0] : value) as Status;
  }
})();

type MerchantProviderProps = {
  merchantId? :string
}

export const { useMerchant, MerchantProvider } = createHookContext('Merchant', ({ merchantId }: MerchantProviderProps) => {

  const navigate = useSmartNavigate();
  const { i18n } = useTranslation();

  const { isLoading, merchantDetails, error, isSlugCheck, navPage } = useGetMerchantDetailsResource(merchantId);

  const merchant = useMemo<MerchantDetails | undefined>(() => {
    if (!merchantDetails) return;

    const { timezone, phone } = merchantDetails;

    return {
      ...merchantDetails,
      timezone: getValidTimezone(timezone),
      phone: isPhoneNumberValid(phone) ? phone : undefined,
    };
  }, [merchantDetails]);

  const locale = merchant?.locale ?? DEFAULT_LOCALE_CODE;
  const [, country = 'US'] = locale.split('-');

  useEffect(() => {
    const i18nCountry = getCountryCode(i18n.language);
    if (merchant) trackMerchant(merchant);

    if (merchant?.locale && country !== i18nCountry) i18n.changeLanguage(merchant?.locale);
  }, [i18n, merchant, country]);

  const pickUpService = useMemo(() => merchant?.services?.find(service => service.type === ServiceType.PICKUP), [merchant]);
  const serviceTypes = useMemo(() => merchant?.services?.filter(({ enabled } : { enabled: boolean }) => enabled), [merchant]);

  useEffect(() => {
    if (merchant && (!isSlug(merchantId) && !isSlugCheck)) {
      navigate(`~/${merchant.slug}`, { replace: true });

      return;
    }

    const giftCardServiceEnabled = merchant?.onlineServices?.GIFT_CARDS?.enabled;
    if ((isNotFoundError(error) && navPage)|| (merchant && !(pickUpService || giftCardServiceEnabled))) {
      navigate('~/not-found');
    }
  }, [error, merchant, merchantId, navigate, pickUpService, isSlugCheck, navPage]);

  const status = useMemo(() => {
    if (STATUS_OVERRIDE) return STATUS_OVERRIDE;

    if (error || (!isLoading && !merchant)) return 'fetchError';

    if (serviceTypes?.length === 0) return 'paused';

    if (Object.entries(merchant?.serviceHoursInformation || {}).findIndex(([, entry]) => entry.serviceHoursState === 'OPEN') > -1) {
      return 'online';
    }

    return 'closed';
  }, [error, isLoading, merchant, serviceTypes]);

  return {
    merchant,
    status,
    service: pickUpService,
    isLoading: (!merchant && !error) || isLoading,
    locale,
    isSettingEnabled: useCallback((settingName) => merchant?.enabledSettings.includes(settingName) || false, [merchant]),
    isCustomerAuthEnabled: useMemo(() => merchant?.enabledSettings.includes(COLO_AUTHENTICATION_ENABLED) || false, [merchant]),
    isGiftcardCheckOutEnabled: useMemo(() => merchant?.paymentMethods.includes(GIFTCARD_PAY_ENABLED) || false, [merchant]),
    isPerksEnabled: useMemo(() => merchant?.enabledSettings.includes(PERKS_ENABLED) || false, [merchant]),
    country: country as CountryCode,
    deliveryMinimumItemCost: useMemo(() => merchant?.deliveryMinimum ? fromCents(merchant.deliveryMinimum ) : 0, [merchant?.deliveryMinimum]),
    deliveryFee: useMemo(() => merchant?.deliveryFee ? fromCents(merchant.deliveryFee ) : 0, [merchant?.deliveryFee]),
    deliveryOrderNoFeesAmount: useMemo(() => merchant?.deliveryOrderNoFeesAmount ? fromCents(merchant.deliveryOrderNoFeesAmount ) : 0, [merchant?.deliveryOrderNoFeesAmount]),
  };

});
